/**
 * PawPlan Hero Widget
 * Shows returning users their saved PawPlan on the homepage hero section.
 *
 * Data flow:
 *   1. Cookie (pets_info) — set when quiz is completed on this browser
 *   2. Shopify customer metafields (synced from Klaviyo) — works cross-device
 *   3. Logged in, no data — prompt to take the quiz
 *   4. Not logged in — prompt to log in
 */
(function () {
  'use strict';

  // ============================================================================
  // CONFIGURATION
  // ============================================================================
  var RESULTS_PAGE_URL = '/pages/pawplan-results';

  // ============================================================================
  // COOKIE HELPERS
  // ============================================================================
  function getCookie(name) {
    var cname = name + '=';
    var decodedCookie = decodeURIComponent(document.cookie);
    var ca = decodedCookie.split(';');
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i].trim();
      if (c.indexOf(cname) === 0) return c.substring(cname.length);
    }
    return '';
  }

  function setCookie(name, value, days) {
    var maxAge = days * 24 * 60 * 60;
    document.cookie = name + '=' + encodeURIComponent(value) + ';path=/;max-age=' + maxAge;
  }

  // ============================================================================
  // PET DATA FROM COOKIE
  // ============================================================================
  function getPetInfoFromCookie() {
    var petsInfoCookie = getCookie('pets_info');
    if (!petsInfoCookie || petsInfoCookie.trim() === '' || petsInfoCookie === '[]') return null;
    try {
      var petsInfo = JSON.parse(petsInfoCookie);
      return petsInfo && petsInfo.length > 0 ? petsInfo[petsInfo.length - 1] : null;
    } catch (e) {
      return null;
    }
  }

  // ============================================================================
  // PET DATA FROM SHOPIFY CUSTOMER METAFIELDS (rendered by Liquid)
  // ============================================================================
  function getPetInfoFromMetafields(container) {
    var petType = (container.dataset.petType || '').trim();
    var petName = (container.dataset.petName || '').trim();

    // Must have at least type or name to be valid
    if (!petType && !petName) return null;

    return {
      type: petType,
      name: petName,
      weight: (container.dataset.petWeight || '').trim(),
      breed: (container.dataset.petBreed || '').trim(),
      birthdate: (container.dataset.petBirthdate || '').trim(),
      address: {
        postcode: (container.dataset.petPostcode || '').trim()
      }
    };
  }

  // ============================================================================
  // HIGH RISK POSTCODE CHECK
  // ============================================================================
  function checkHighRiskPostcode(postcode) {
    if (!postcode) return false;
    if (typeof window.isHighRiskPostcode === 'function') {
      return window.isHighRiskPostcode(postcode);
    }
    var hrPrefixes = ['08', '09', '48', '49', '28', '29', '47', '68', '69'];
    return hrPrefixes.some(function (prefix) { return String(postcode).startsWith(prefix); });
  }

  // ============================================================================
  // PRODUCT HANDLE CALCULATOR
  // ============================================================================
  function calculateProductHandle(petInfo) {
    var petType = (petInfo.type || '').toLowerCase();
    var weight = parseFloat(petInfo.weight) || 0;
    var postcode = (petInfo.address && petInfo.address.postcode) || petInfo.postcode || '';
    var isHighRisk = checkHighRiskPostcode(postcode);

    if (petType === 'dog') {
      if (isHighRisk) {
        if (weight >= 1.3 && weight <= 2.5) return 'simparica-trio-yellow-puppy';
        if (weight >= 2.6 && weight <= 5) return 'simparica-trio-purple-small-dogs-2-6-5kg-3-pack';
        if (weight >= 5.1 && weight <= 10) return 'simparica-trio-orange-small';
        if (weight >= 10.1 && weight <= 20) return 'simparica-trio-blue-medium';
        if (weight >= 20.1 && weight <= 40) return 'simparica-trio-green-large';
        if (weight >= 40.1 && weight <= 60) return 'simparica-trio-red-xl-3pk';
        if (weight < 1.3) return 'simparica-trio-yellow-puppy';
        return 'simparica-trio-red-xl-3pk';
      } else {
        if (weight < 4) return 'flea-treatment-xsmall-dog';
        if (weight >= 4 && weight <= 10) return 'flea-treatment-small-dog';
        if (weight > 10 && weight <= 25) return 'flea-treatment-medium-dog';
        return 'flea-treatment-large-dog';
      }
    }

    if (petType === 'cat') {
      return weight < 4 ? 'flea-treatment-small-cat' : 'flea-treatment-medium-cat';
    }

    return null;
  }

  // ============================================================================
  // BUILD RESULTS PAGE URL
  // ============================================================================
  function buildResultsUrl(petInfo) {
    var productHandle = calculateProductHandle(petInfo);
    if (productHandle) {
      return RESULTS_PAGE_URL + '?product=' + encodeURIComponent(productHandle);
    }
    return RESULTS_PAGE_URL;
  }

  // ============================================================================
  // FETCH MAIN PRODUCT IMAGE
  // ============================================================================
  function fetchProductImage(handle) {
    return fetch('/products/' + handle + '.js')
      .then(function (response) {
        if (!response.ok) return null;
        return response.json();
      })
      .then(function (product) {
        if (!product) return null;
        return product.featured_image || (product.images && product.images[0]) || null;
      })
      .catch(function () { return null; });
  }

  // ============================================================================
  // RENDER — Returning user with pet data (cookie or metafields)
  // ============================================================================
  function renderReturningUserWidget(container, petInfo) {
    var petName = petInfo.name || 'Your Pet';
    var resultsUrl = buildResultsUrl(petInfo);
    var productHandle = calculateProductHandle(petInfo);

    container.innerHTML =
      '<div class="pawplan-hero-widget pawplan-hero-widget--loaded" id="pawplan-hero-widget">' +
      '<div class="pawplan-hero-widget__content">' +
      '<div class="pawplan-hero-widget__divider"><span>already have a plan?</span></div>' +
      '<p class="pawplan-hero-widget__greeting">Welcome back!</p>' +
      '<h3 class="pawplan-hero-widget__title">' +
      '<span class="pawplan-hero-widget__pet-name">' + petName + '\'s</span> PawPlan is ready' +
      '</h3>' +
      '<div class="pawplan-hero-widget__product-preview" id="pawplan-product-preview">' +
      '<div class="pawplan-hero-widget__preview-shimmer"></div>' +
      '</div>' +
      '<a href="' + resultsUrl + '" class="pawplan-hero-widget__cta" id="pawplan-hero-cta">' +
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">' +
      '<path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/>' +
      '</svg>' +
      'See ' + petName + '\'s Plan' +
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="14" height="14">' +
      '<polyline points="9 18 15 12 9 6"></polyline>' +
      '</svg>' +
      '</a>' +
      '</div>' +
      '</div>';

    if (productHandle) {
      fetchProductImage(productHandle).then(function (imageUrl) {
        var previewEl = document.getElementById('pawplan-product-preview');
        if (previewEl && imageUrl) {
          previewEl.innerHTML =
            '<img src="' + imageUrl + '" alt="Recommended product" class="pawplan-hero-widget__preview-img" loading="lazy">' +
            '<span class="pawplan-hero-widget__preview-label">Your recommended protection</span>';
        } else if (previewEl) {
          previewEl.remove();
        }
      });
    }
  }

  // ============================================================================
  // RENDER — Logged-in user, no pet data anywhere
  // ============================================================================
  function renderLoggedInNoCookieWidget(container, customerName) {
    var greeting = customerName ? ('Welcome back, ' + customerName + '!') : 'Welcome back!';

    container.innerHTML =
      '<div class="pawplan-hero-widget pawplan-hero-widget--loaded" id="pawplan-hero-widget">' +
      '<div class="pawplan-hero-widget__content">' +
      '<div class="pawplan-hero-widget__divider"><span>new here?</span></div>' +
      '<p class="pawplan-hero-widget__greeting">' + greeting + '</p>' +
      '<p class="pawplan-hero-widget__sub-text">Complete the quiz to see your personalised PawPlan</p>' +
      '</div>' +
      '</div>';
  }

  // ============================================================================
  // RENDER — Not logged in, no cookie → login prompt
  // ============================================================================
  function renderLoginPrompt(container) {
    container.innerHTML = `
    <div class="pawplan-hero-widget pawplan-hero-widget--login" id="pawplan-hero-widget">
      <div class="pawplan-hero-widget__content">
        <div class="pawplan-hero-widget__divider">
          <span>already have a plan?</span>
        </div>
        <p class="pawplan-hero-widget__greeting">Already a PawPlan member?</p>
        <a href="/customer_authentication/login?return_to=/" class="pawplan-hero-widget__cta pawplan-hero-widget__cta--login">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" />
          </svg>
          Log In for Your PawPlan
        </a>
      </div>
    </div>
  `;
  }

  // ============================================================================
  // MAIN INITIALIZATION
  // ============================================================================
  function initHeroWidget() {
    var container = document.getElementById('pawplan-hero-widget-container');
    if (!container) return;

    var customerEmail = (container.dataset.customerEmail || '').trim();
    var customerName = (container.dataset.customerName || '').trim();
    var isLoggedIn = customerEmail.length > 0;

    // 1. Check cookie first (fastest — works for any user)
    var cookiePetInfo = getPetInfoFromCookie();
    if (cookiePetInfo) {
      renderReturningUserWidget(container, cookiePetInfo);
      return;
    }

    // 2. Logged in — check Shopify customer metafields (synced from Klaviyo)
    if (isLoggedIn) {
      var metafieldPetInfo = getPetInfoFromMetafields(container);
      if (metafieldPetInfo) {
        // Save to cookie so future page loads use the fast cookie path
        try {
          setCookie('pets_info', JSON.stringify([metafieldPetInfo]), 30);
        } catch (e) { }
        renderReturningUserWidget(container, metafieldPetInfo);
        return;
      }

      // Logged in but no pet data anywhere → prompt to take quiz
      renderLoggedInNoCookieWidget(container, customerName);
      return;
    }

    // 3. Not logged in, no cookie → login prompt
    renderLoginPrompt(container);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeroWidget);
  } else {
    initHeroWidget();
  }

  // Listen for quiz completion events to update the widget
  document.addEventListener('pawplan:pet-updated', function (e) {
    var container = document.getElementById('pawplan-hero-widget-container');
    if (container && e.detail && e.detail.petInfo) {
      renderReturningUserWidget(container, e.detail.petInfo);
    }
  });

  // Expose for external use
  window.PawPlanHeroWidget = { init: initHeroWidget };
})();
